// src/api/userListsApi.ts
import {SavedEventPolicyView} from '../types/SavedEventPolicyView';
import {refreshAccessToken, tokenStore} from './client';

/** ===== Access Token Helpers ===== */
function getAccessToken(): string | null {
  return tokenStore.get();
}

/** ===== UserId Header Support (X-User-Id) ===== */
let _userIdForLists: number | null = null;

export function setUserIdForLists(userId: number) {
  _userIdForLists = Number.isFinite(userId) ? userId : null;
}

/** ===== 공통 fetchJson with auth & centralized auto-refresh ===== */
async function fetchJson(
    input: RequestInfo,
    init?: RequestInit
): Promise<any> {
  const devId = localStorage.getItem('X_DEV_USER_ID');
  let token = getAccessToken();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(devId ? {'X-Dev-User-Id': devId} : {}),
    ...(_userIdForLists
        ? {'X-User-Id': String(_userIdForLists)}
        : {}),
    ...(token ? {Authorization: `Bearer ${token}`} : {}),
    ...(init?.headers as Record<string, string>),
  };

  const doFetch = () =>
      fetch(input, {
        ...init,
        credentials: 'include',
        headers,
      });

  let response = await doFetch();

  if (response.status === 401 || response.status === 419) {
    const newToken = await refreshAccessToken();
    token = newToken;
    headers.Authorization = `Bearer ${newToken}`;
    response = await doFetch();
  }

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(
        `HTTP ${response.status} ${response.statusText} ${text}`
    );
  }

  if (response.status === 204) return null;
  return response.json();
}

/** ===== Array normalizer ===== */
function pickArray<T = any>(obj: any): T[] {
  if (!obj) return [];
  if (Array.isArray(obj)) return obj as T[];
  if (Array.isArray(obj.items)) return obj.items as T[];
  if (Array.isArray(obj.data)) return obj.data as T[];
  if (Array.isArray(obj.content)) return obj.content as T[];
  if (Array.isArray(obj.results)) return obj.results as T[];
  return [];
}

/** ===== Key getter 유틸 ===== */
const g = (o: any, keys: string[]) => {
  if (!o) return undefined;
  for (const k of keys) {
    if (
        k in o &&
        o[k] !== undefined &&
        o[k] !== null &&
        o[k] !== ''
    ) {
      return o[k];
    }
  }
  return undefined;
};

/** ===== Types ===== */
export type SavedEventLite = {
  eventId: string;
  title: string;
  sourceUrl?: string;
  publishedAt?: string;
};

export type WatchItem = {
  code: string;
  name?: string;
  market?: string;
};

export type AlertItem = {
  id: string;
  name: string;
  isActive: boolean;
};

export type RemoveInterestPayload = {
  feedId: string;
  sector: string;
};

/** ===== API: 저장한 이벤트 목록 ===== */
export async function getMySavedEvents(): Promise<SavedEventPolicyView[]> {
  const raw = await fetchJson('/api/v1/users/me/saved-events');
  const arr = pickArray<any>(raw);

  return arr.map(
      (r: any): SavedEventPolicyView => ({
        feed_id: String(
            r.feed_id ?? r.event_id ?? r.id ?? ''
        ),
        sector: r.sector ?? null,
        title: r.title ?? null,
        title_ko: r.title_ko ?? null,
        summary: r.summary ?? null,
        summary_ko: r.summary_ko ?? null,
        published_at: r.published_at ?? null,
        source_urls:
            r.source_urls ??
            r.source_url ??
            null,
        policy_summary: r.policy_summary ?? null,
        gpt_analysis: r.gpt_analysis ?? null,
        investment_note: r.investment_note ?? null,
        impact_analysis: r.impact_analysis ?? null,
        issue_tags: r.issue_tags ?? null,
        provenance_reason: r.provenance_reason ?? null,
        direct_kor: r.direct_kor ?? null,
        indirect_kor: r.indirect_kor ?? null,
        related_kor: r.related_kor ?? null,
        data_provenance: r.data_provenance ?? null,
      })
  );
}

/** ===== API: 워치리스트 ===== */
export async function getMyWatchlist(): Promise<WatchItem[]> {
  const raw = await fetchJson('/api/v1/users/me/watchlist');
  const arr = pickArray<any>(raw);

  const seen = new Set<string>();

  return arr
  .map((it: any) => {
    const code = String(
        g(it, ['code', 'ticker', 'symbol']) ?? ''
    );
    const nameRaw = g(it, [
      'name',
      'kor_name',
      'eng_name',
    ]);
    const marketRaw = g(it, [
      'market',
      'exchange',
    ]);

    return {
      code,
      name: nameRaw ? String(nameRaw) : code,
      market: marketRaw
          ? String(marketRaw)
          : undefined,
    } as WatchItem;
  })
  .filter(
      (watchItem) =>
          watchItem.code &&
          !seen.has(watchItem.code) &&
          (seen.add(watchItem.code), true)
  );
}

/** ===== API: 내 알림 목록 ===== */
export async function getMyAlerts(): Promise<AlertItem[]> {
  const raw = await fetchJson('/api/v1/users/me/alerts');
  const arr = pickArray<any>(raw);

  return arr
  .map((it: any) => ({
    id: String(g(it, ['id', 'alertId', 'alert_id'])),
    name: String(g(it, ['name', 'title']) ?? '알림'),
    isActive: Boolean(
        g(it, ['isActive', 'active', 'enabled'])
    ),
  }))
  .filter((alert) => Boolean(alert.id));
}

/** ===== API: 저장한 이벤트 삭제 ===== */
export async function deleteSavedEventById(
    id: string | number
): Promise<void> {
  await fetchJson(
      `/api/v1/users/me/saved-events/${id}`,
      {
        method: 'DELETE',
      }
  );
}

export async function deleteSavedEventBySectorAndFeedId(
    payload: RemoveInterestPayload
): Promise<void> {
  const feedId = String(payload?.feedId ?? '').trim();
  const sector = String(payload?.sector ?? '').trim();

  if (!feedId) throw new Error('feedId is empty');
  if (!sector) throw new Error('sector is empty');

  await fetchJson(
      `/api/v1/users/me/saved-events/${encodeURIComponent(sector)}/${encodeURIComponent(feedId)}`,
      {
        method: 'DELETE',
      }
  );
}

/** ===== API: 알림 삭제 ===== */
export async function deleteAlertById(
    id: string | number
): Promise<void> {
  await fetchJson(
      `/api/v1/users/me/alerts/${id}`,
      {
        method: 'DELETE',
      }
  );
}

export async function removeInterestByKey(
    payload: RemoveInterestPayload
): Promise<void> {
  const feedId = String(payload?.feedId ?? '').trim();
  const sector = String(payload?.sector ?? '').trim();

  if (!feedId) throw new Error('feedId is empty');
  if (!sector) throw new Error('sector is empty');

  await fetchJson(
      `/api/v1/users/me/saved-events/${encodeURIComponent(sector)}/${encodeURIComponent(feedId)}`,
      {
        method: 'DELETE',
      }
  );
}

export async function saveEventForUser(
    feedId: string | number,
    sector?: string
) {
  return fetchJson('/api/v1/users/me/saved-events', {
    method: 'POST',
    body: JSON.stringify({
      feedId: String(feedId),
      sector: sector ?? null,
    }),
  });
}

export async function unsaveEventForUser(
    feedId: string | number
) {
  return fetchJson(
      `/api/v1/users/me/saved-events/${feedId}`,
      {
        method: 'DELETE',
      }
  );
}
